import {Component, OnInit} from '@angular/core';
import {ApiService} from '../../core/http/api.service';
import {UtilsService} from '../../core/services/utils/utils.service';
import {forkJoin} from 'rxjs';

interface ManualAssignmentForm {
  userId: string;
  projectId: string;
  roleId: string;
  notes: string;
}

@Component({
    selector: 'app-marketplace-project-applications',
    templateUrl: './marketplace-project-applications.component.html',
    styleUrls: ['./marketplace-project-applications.component.css'],
    standalone: false
})
export class MarketplaceProjectApplicationsComponent implements OnInit {

  loading = true;
  actionLoadingId = '';
  manualSaving = false;
  showManualAssignModal = false;
  page = 1;
  perPage = 10;
  loaders = Array(10).fill(0);
  applications: any[] = [];
  projects: any[] = [];
  availableRoles: any[] = [];
  manualForm: ManualAssignmentForm = this.createEmptyManualForm();
  filters = {
    keyword: '',
    status: 'all',
    itemType: 'all'
  };

  constructor(
    private api: ApiService,
    private utils: UtilsService
  ) {
  }

  ngOnInit(): void {
    this.getProjects();
    this.getApplications();
  }

  createEmptyManualForm(): ManualAssignmentForm {
    return {
      userId: '',
      projectId: '',
      roleId: '',
      notes: ''
    };
  }

  get filteredApplications(): any[] {
    const keyword = (this.filters.keyword || '').trim().toLowerCase();
    const status = this.filters.status;
    const itemType = this.filters.itemType;

    return (this.applications || []).filter(application => {
      const appItemType = String(application?.marketplaceItemType || 'project')
        .trim()
        .toLowerCase();
      const matchesKeyword = !keyword || [
        application?.userName,
        application?.email,
        application?.projectName,
        application?.roleName,
        application?.requestDetails
      ].some(value => String(value || '').toLowerCase().includes(keyword));

      const matchesStatus = status === 'all' || application?.status === status;
      const matchesItemType = itemType === 'all' || appItemType === itemType;

      return matchesKeyword && matchesStatus && matchesItemType;
    });
  }

  getProjects(): void {
    forkJoin([
      this.api.getMarketplaceProjects({includeInactive: true}),
      this.api.getMarketplaceSatires({includeInactive: true})
    ]).subscribe(([projectRes, satireRes]) => {
      const projectList = Array.isArray(projectRes?.data) ? projectRes.data : [];
      const satireList = Array.isArray(satireRes?.data) ? satireRes.data : [];
      this.projects = [...projectList, ...satireList];
    }, err => this.utils.resErr(err));
  }

  getApplications(): void {
    this.loading = true;
    const query: any = {};
    if (this.filters.status !== 'all') {
      query.status = this.filters.status;
    }

    this.api.getMarketplaceProjectApplications(query).subscribe(res => {
      this.applications = (Array.isArray(res?.data) ? res.data : []).map(application => {
        const workflowStatus = String(application?.status || 'pending').toLowerCase();
        const marketplaceItemType = String(application?.marketplaceItemType || 'project')
          .trim()
          .toLowerCase();
        return {
          ...application,
          workflowStatus,
          marketplaceItemType: marketplaceItemType === 'satire' ? 'satire' : 'project',
          status: workflowStatus === 'completed' ? 'approved' : workflowStatus
        };
      });
      this.loading = false;
    }, err => {
      this.loading = false;
      this.utils.resErr(err);
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.getApplications();
  }

  clearFilters(): void {
    this.filters = {
      keyword: '',
      status: 'all',
      itemType: 'all'
    };
    this.page = 1;
    this.getApplications();
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.keyword || '').trim()
      || this.filters.status !== 'all'
      || this.filters.itemType !== 'all';
  }

  handleFilterInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.applyFilters();
    }
  }

  updateApplicationStatus(application, status: string): void {
    if (this.actionLoadingId) {
      return;
    }

    this.actionLoadingId = application.id;
    const payload: any = { status };
    if (status === 'approved') {
      payload.status = 'completed';
      payload.createMembership = true;
    }

    this.api.updateMarketplaceProjectApplication(application.id, payload).subscribe(() => {
      this.actionLoadingId = '';
      this.utils.toastSuccess('Request status updated successfully');
      this.getApplications();
    }, err => {
      this.actionLoadingId = '';
      this.utils.resErr(err);
    });
  }

  openManualAssignModal(application: any = null): void {
    this.manualForm = this.createEmptyManualForm();
    this.availableRoles = [];

    if (application) {
      this.manualForm.userId = application.userId || '';
      this.manualForm.projectId = application.marketplaceItemId || '';
      this.manualForm.roleId = application.roleId || '';
      this.manualForm.notes = application.adminNotes || '';
      this.updateAvailableRoles(this.manualForm.projectId);
    }

    this.showManualAssignModal = true;
  }

  closeManualAssignModal(): void {
    if (this.manualSaving) {
      return;
    }
    this.showManualAssignModal = false;
    this.manualForm = this.createEmptyManualForm();
    this.availableRoles = [];
  }

  updateAvailableRoles(projectId: string = ''): void {
    const selectedProject = this.projects.find(project => project.id === projectId);
    this.availableRoles = selectedProject?.roles || [];
    const roleExists = this.availableRoles.some(role => role.id === this.manualForm.roleId);
    if (!roleExists) {
      this.manualForm.roleId = '';
    }
  }

  saveManualAssignment(): void {
    if (this.manualSaving) {
      return;
    }

    if (!(this.manualForm.userId || '').trim()) {
      this.utils.toastError('Please enter user id');
      return;
    }
    if (!(this.manualForm.projectId || '').trim()) {
      this.utils.toastError('Please select project');
      return;
    }
    if (!(this.manualForm.roleId || '').trim()) {
      this.utils.toastError('Please select role');
      return;
    }

    this.manualSaving = true;
    const payload = {
      userId: this.manualForm.userId.trim(),
      projectId: this.manualForm.projectId,
      roleId: this.manualForm.roleId,
      notes: (this.manualForm.notes || '').trim(),
    };

    this.api.addUserMarketplaceProjectMembership(payload).subscribe(() => {
      this.manualSaving = false;
      this.utils.toastSuccess('User added to role successfully');
      this.closeManualAssignModal();
      this.getApplications();
    }, err => {
      this.manualSaving = false;
      this.utils.resErr(err);
    });
  }

  pageChange(event): void {
    this.page = event;
    window.scrollTo(0, 0);
  }

  handleCopyToClipboard(str: any = ''): void {
    this.utils.copyToClipboard(str);
    this.utils.toastSuccess('ID copied to your clipboard successfully');
  }
}
